import { fontSizes, fontWeights, lineHeight } from '@ohgoodlord/design-system'
import styled from 'styled-components'
import { colors } from '../../../common/colors'
import { Section } from './section'
import { FastestTag } from './fastest-tag'
import { HighlightCallout } from './highlight-callout'
import { IncomeTypeChip } from './income-type-chip'
import { Subheading, Subsection, SupportingText } from './styles'

const SELF_EMPLOYED_HELP_URL =
  'https://contact.goodlord.co/tag/s/article/Income-How-we-verify-your-self-employment'

const OTHER_INCOME_TYPES = [
  'Savings / independent means',
  'Director',
  'Foster care income',
  'Investments',
  'Maternity and paternity income',
  'Statutory Sick Pay (SSP)',
  'Students with an incoming bursary or scholarship',
  'Sub-contractors / CIS',
]

const DefinitionTitle = styled.p`
  margin: 0;
  font-size: ${fontSizes.m};
  font-weight: ${fontWeights.bold};
  line-height: ${lineHeight};
  color: ${colors.navyBlue};
  display: flex;
  align-items: center;
  gap: 10px;
`

const DefinitionDescription = styled.p`
  margin: 0;
  font-size: ${fontSizes.s};
  font-weight: ${fontWeights.normal};
  line-height: ${lineHeight};
  color: ${colors.greyscale2};
`

const DefinitionStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`

const DefinitionGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 9px;
  width: 100%;
  padding-bottom: 9px;

  &:last-child {
    padding-bottom: 0;
  }
`

const DividerBlock = styled.div`
  border-top: 1px solid #eef2f5;
  padding-top: 13px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`

const IncomeTypeChipList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
  width: 100%;
`

const ExternalTextLink = styled.a`
  color: ${colors.greyscale2};
  text-decoration: underline;
`

const MutedSupportingText = styled(SupportingText)`
  color: ${colors.greyscale3};
`

const OpenBankingDescription = () => (
  <DefinitionDescription>
    A secure open banking connection allows us to verify your income in{' '}
    <strong>less than 60 seconds</strong>.
  </DefinitionDescription>
)

export const Income = () => {
  return (
    <Section title="Income">
      <HighlightCallout
        variant="amber"
        title="Please provide original PDF documents where possible."
        description="Screenshots are not accepted and photos may delay your application."
      />
      <MutedSupportingText>Find the section that applies to you.</MutedSupportingText>

      <Subsection>
        <Subheading>Employed by a company</Subheading>
        <DefinitionStack>
          <DefinitionGroup>
            <DefinitionTitle>
              Open Banking
              <FastestTag />
            </DefinitionTitle>
            <OpenBankingDescription />
          </DefinitionGroup>
          <DefinitionGroup>
            <DefinitionTitle>HMRC / Payroll connection</DefinitionTitle>
            <DefinitionDescription>
              Allowing us to verify your income securely and instantly.
            </DefinitionDescription>
          </DefinitionGroup>
          <DefinitionGroup>
            <DefinitionTitle>Employment contract</DefinitionTitle>
            <DefinitionDescription>
              Only needed if you've been at the company for less than 3 months or are on a
              fix term contract.
            </DefinitionDescription>
          </DefinitionGroup>
        </DefinitionStack>
      </Subsection>

      <DividerBlock>
        <Subheading>Self-employed</Subheading>
        <DefinitionStack>
          <DefinitionGroup>
            <DefinitionTitle>Tax return & proof of submission</DefinitionTitle>
            <DefinitionDescription>
              Proof of submission will need to be a separate document if it's not shown in
              the tax return.{' '}
              <ExternalTextLink
                href={SELF_EMPLOYED_HELP_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Find out more
              </ExternalTextLink>
            </DefinitionDescription>
          </DefinitionGroup>
        </DefinitionStack>
      </DividerBlock>

      <DividerBlock>
        <Subheading>Pensions</Subheading>
        <DefinitionStack>
          <DefinitionGroup>
            <DefinitionTitle>
              Open Banking
              <FastestTag />
            </DefinitionTitle>
            <OpenBankingDescription />
          </DefinitionGroup>
        </DefinitionStack>
      </DividerBlock>

      <DividerBlock>
        <Subheading>Benefits</Subheading>
        <DefinitionStack>
          <DefinitionGroup>
            <DefinitionTitle>
              Open Banking
              <FastestTag />
            </DefinitionTitle>
            <OpenBankingDescription />
          </DefinitionGroup>
        </DefinitionStack>
      </DividerBlock>

      <DividerBlock>
        <Subheading>Other income types</Subheading>
        <SupportingText>We also assess other income types including:</SupportingText>
        <IncomeTypeChipList>
          {OTHER_INCOME_TYPES.map(type => (
            <IncomeTypeChip key={type} label={type} />
          ))}
        </IncomeTypeChipList>
        <MutedSupportingText>
          The documents needed for these income types will be explained if or when they
          become relevant to your application.
        </MutedSupportingText>
      </DividerBlock>
    </Section>
  )
}
